import { Injectable, Injector } from '@angular/core';

import { MessageService } from 'primeng/api';
import { BehaviorSubject, catchError, map, Observable, of, Subscription, switchMap, tap } from 'rxjs';

import { ContactsPublicApi } from '../../../api/targeting/contacts-public.api';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import {
    EmailComposerOvertakeComponent
} from '../../../Common/RhpContexts/components/InteractionViewTab/EmailComposerOvertake/email-composer-overtake.component';
import { EmailComposerModeEnum } from '../../../Common/RhpContexts/enums/email-composer-mode.enum';
import { RepliesSearchApi } from '../../../api/targeting/model/replies-search-api';
import { NylasMessageModel } from '../models/nylas-message.model';
import { EmailAccountConnectorMessageModel } from '../models/email-account-connector-message.model';
import { DistributionService } from '../../../Common/TargetingShared/services/distribution.service';
import { TargetingRhpContextsService } from '../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { NylasLocalStorageMessageModel, NylasMessageLocalStorage } from '../models/nylas-message-local-storage';


@Injectable()
export class EmailConversationService  {
    contactId: string;
    repliesSearch: RepliesSearchApi;

    private loadingSubject = new BehaviorSubject<boolean>(false);
    loading$ = this.loadingSubject.asObservable();

    private refreshConversationSubject = new BehaviorSubject<boolean>(false);

    private modeSubject = new BehaviorSubject<EmailComposerModeEnum>(null);
    mode$ = this.modeSubject.asObservable();

    messages$ = this.refreshConversationSubject.pipe(switchMap(refresh => {
        if (refresh) {
            this.loadingSubject.next(true);
            return this.contactsPublicApi.getDistributionMessage(this.repliesSearch, this.contactId).pipe(
                map(nylasThread => {
                    return nylasThread?.nylasMessages?.sort((a, b) => b.date - a.date).map(x => new NylasMessageModel(x));
                }),
                tap(x => {
                    this.loadingSubject.next(false);
                }),
                catchError(err => {
                    this.loadingSubject.next(false);
                    throw err;
                })
            );
        } else {
            return of([] as NylasMessageModel[]);
        }
    }));

    private subscriptions: Subscription[] = [];

    constructor(
        public contactsPublicApi: ContactsPublicApi,
        public currentUserStateService: CurrentUserStateService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
        private injector: Injector,
        private distributionService: DistributionService,
        private targetingRhpContextsService: TargetingRhpContextsService
    ) {
        this.subscriptions.push( );
    }

    viewConversation(repliesSearch: RepliesSearchApi, contactId: string): void {
        const oldContactId = this.contactId;
        this.contactId = contactId;
        this.repliesSearch = repliesSearch;
        this.modeSubject.next(EmailComposerModeEnum.CONVERSATION);
        this.refreshConversation();

        if (oldContactId !== contactId) {
            this.targetingRhpContextsService.setContact(contactId);
            const overlayContainer = document.getElementById('main-content');
            const animationTrigger = new BehaviorSubject(
                new FullProfileAnimationModel(
                    FullProfileDialogAnimationsEnum.slideInOutAnimation,
                    OverlayAnimationStateEnum.open
                )
            );

            const ioModel =  {
                inputs: {
                    animationTrigger: animationTrigger,
                    overlayContainer: overlayContainer
                },
                outputs: {
                    dialogClose: () => {
                    }
                }
            };
            this.coreDialogService.open(EmailComposerOvertakeComponent, ioModel, this.injector);
        }

    }

    composeEmail(contactId: string): void {
        this.contactId = contactId;
        this.targetingRhpContextsService.setContact(contactId);
        this.modeSubject.next(EmailComposerModeEnum.COMPOSE);

        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            },
            outputs: {
                dialogClose: () => {
                }
            }
        };

        this.coreDialogService.open(EmailComposerOvertakeComponent, ioModel, this.injector);
    }

    refreshConversation(refresh: boolean = true): void {
        this.refreshConversationSubject.next(refresh);
    }

    sendEmail(emailMessage: EmailAccountConnectorMessageModel): Observable<void> {
        return this.contactsPublicApi.directMessage(emailMessage.toApiModel(), this.contactId);
    }

    replyDistributionMessage(emailMessage: EmailAccountConnectorMessageModel): Observable<void> {
        return this.distributionService.getDistributionById(this.repliesSearch.distributionId).pipe(switchMap(distribution => {
            emailMessage.sendFrom = distribution.sendFrom;
            return this.contactsPublicApi.directMessage(emailMessage.toApiModel(), this.contactId);
        }));
    }

    readMessagesFromLocalStorage(messages: NylasMessageModel[], accountId: number): NylasLocalStorageMessageModel[] {
        const incomingMessages = messages.map(message => NylasLocalStorageMessageModel.fromNylasMessageModel(message, this.contactId, accountId));
        const currentStorage = new NylasMessageLocalStorage();
        const accountMessages = currentStorage.getLocalStorageNylasMessagesForAccount(accountId);

        // Local Storage messages that do not exist (yet) in the response call messages - ie., pending
        const unmatchedMessages = accountMessages
            .filter(accountMessage => !incomingMessages.some(incomingMessage => incomingMessage.compareMessages(accountMessage)));

        // Matched messages are no longer pending, and can be removed
        currentStorage.setLocalStorageNylasMessagesForAccount(accountId, unmatchedMessages);
        currentStorage.saveLocalStorage();

        return unmatchedMessages;
    }

    storeMessageInLocalStorage(emailMessage: EmailAccountConnectorMessageModel, accountId: number) {
        const localStorageMessage = NylasLocalStorageMessageModel.fromEmailAccountConnectorMessage(emailMessage, this.contactId, accountId);
        const currentStorage = new NylasMessageLocalStorage();
        currentStorage.addMessageToLocalStorage(localStorageMessage);
        currentStorage.saveLocalStorage();
    }

    reset() {
        this.contactId = null;
        this.repliesSearch = null;
        this.refreshConversation(false);
        this.loadingSubject.next(false);
    }
}
